// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FancyScroll from "@guestsupply/App/Components/Common/FancyScroll";
import XngZone from "@guestsupply/App/Components/Common/XngZone";
import Notify from "@guestsupply/App/Components/Notify/Notify";
import loadNewsList from "@guestsupply/App/Handlers/NewsList/LoadNewsList";
import { GetNewsListApiParameter } from "@guestsupply/App/Services/NewsListService";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getNewsList, getNewsListParameter } from "@guestsupply/App/Store/Selectors/NewsListSelectors";
import { ButtonPresentationProps } from "@guestsupply/MobiusOverrides/Button/Props";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import { Button, GridContainer, GridItem, Hidden, Icon, LazyImage } from "@insite/mobius/index";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

const enum fields {
    defaultPageSize = "defaultPageSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: WidgetProps) => {
    let parameter = getNewsListParameter(state);
    parameter = Object.keys(parameter || {}).length
        ? parameter
        : { page: 1, pageSize: ownProps.fields.defaultPageSize };
    const pages = getNewsList(state, parameter!);
    const pagesCount = getNewsList(state, { page: 1 }).value?.length || 0;
    const allPages = [];
    for (let i = 1; i <= (parameter?.page || 1); i++) {
        allPages.push(...(getNewsList(state, { ...parameter, page: i }).value || []));
    }
    return {
        location: getLocation(state),
        pages,
        allPages,
        parameter,
        pagesCount,
    };
};

const mapDispatchToProps = {
    loadNewsList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    // Xng-Changes:
    container?: GridContainerProps;
    zoneAndListGridItem?: GridItemProps;
    zoneWrapper?: InjectableCss;
    categoryGridItem?: GridItemProps;
    categoryHidden?: HiddenProps;
    categoryWrapper?: InjectableCss;
    categoryTitle?: TypographyProps;
    categoryLinksFancyScroll?: InjectableCss;
    categoryLinksContainer?: InjectableCss;
    categoryLinkWrapper?: InjectableCss;
    selectedCategoryLinkWrapper?: InjectableCss;
    categoryLink?: LinkPresentationProps;
    selectedCategoryLink?: LinkPresentationProps;
    listTitle?: TypographyProps;
    category?: TypographyProps;
    listCategoryLink?: LinkPresentationProps;
    tags?: TypographyProps;
    tagLink?: TypographyProps;
    contentWrapper?: InjectableCss;
    contentSummaryWrapper?: InjectableCss;
    summaryPlaceholder?: InjectableCss;
    newsListImageWrapper?: InjectableCss;
    newsListImage?: LazyImageProps;
    linkStyles?: LinkPresentationProps;
    newsListDetails?: InjectableCss;
    authorBy?: TypographyProps;
    loadMore?: ButtonPresentationProps;
    loadMoreIcon?: IconPresentationProps;
    notify?: TypographyProps;
    categoryCount?: TypographyProps;
}

// Xng-Changes: please check NewsListViewExtension.ts for styles
export const newsListViewStyles: NewsListViewStyles = {};

const styles = newsListViewStyles;

const NewsListView = ({
    id,
    pages,
    allPages,
    parameter,
    history,
    fields: { defaultPageSize },
    location: { pathname, search },
    loadNewsList,
    pagesCount,
}: Props) => {
    const [selectedCategorty, setSelectedCategorty] = React.useState<string | undefined>(undefined);
    const currentPage = React.useRef(1);

    const refElement = React.createRef<HTMLDivElement>();

    const dispatch = useDispatch();

    const updateQueryString = (parameter: GetNewsListApiParameter) => {
        let newLocation = pathname;
        if (parameter.categories) {
            newLocation += `?categories=${parameter.categories}`;
        }
        history.push(newLocation);
    };

    const updateParameter = (newParameter: GetNewsListApiParameter) => {
        newParameter = {
            page: 1,
            pageSize: defaultPageSize,
            categories: selectedCategorty,
            ...newParameter,
        };
        dispatch({
            type: "xNewsList/UpdateParameter",
            parameter: newParameter,
        } as any);
    };

    const filterByCategory = (category: string | undefined) => {
        category = category === selectedCategorty ? undefined : category;
        currentPage.current = 1;
        setSelectedCategorty(category);
        updateParameter({ categories: category });
    };

    useEffect(() => {
        const parsedQuery = parseQueryString<GetNewsListApiParameter>(search);
        if (parsedQuery.categories) {
            setSelectedCategorty(parsedQuery.categories);
        }
        if (!pages.value && !pages.isLoading) {
            dispatch({
                type: "xNewsList/UpdateParameter",
                parameter: { page: 1, pageSize: defaultPageSize, categories: selectedCategorty, ...parsedQuery },
            } as any);
        }
    }, [search]);

    useEffect(() => {
        if (parameter) {
            updateQueryString(parameter);
            if (!pages.value && !pages.isLoading) {
                loadNewsList(parameter);
            }
        }
    }, [parameter]);

    React.useEffect(() => {
        loadNewsList({ page: 1 });
    }, []);

    const categories: any = pages.value ? pages?.properties?.categories : {};
    const categorySelector = (
        <StyledWrapper {...styles.categoryWrapper}>
            <Typography {...styles.categoryTitle}>{translate("Categories")}</Typography>
            <FancyScroll refElement={refElement} extendedStyles={styles.categoryLinksFancyScroll}>
                <StyledWrapper {...styles.categoryLinksContainer}>
                    {Object.keys(categories).map((category, index) => (
                        <StyledWrapper
                            key={`${category}_${index}`}
                            onClick={() => filterByCategory(category)}
                            {...(category === selectedCategorty
                                ? styles.selectedCategoryLinkWrapper
                                : styles.categoryLinkWrapper)}
                        >
                            <Link
                                {...(category === selectedCategorty
                                    ? styles.selectedCategoryLink
                                    : styles.categoryLink)}
                            >
                                {category}
                                <Typography {...styles.categoryCount}>({categories[category]})</Typography>
                            </Link>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </FancyScroll>
        </StyledWrapper>
    );
    const showLoadMore = selectedCategorty
        ? defaultPageSize * currentPage.current < categories[selectedCategorty]
        : defaultPageSize * currentPage.current < pagesCount;

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.zoneAndListGridItem}>
                <StyledWrapper {...styles.zoneWrapper}>
                    <XngZone contentId={id} zoneName="FeaturedArticle" hint="Zone For Featured News Article" />
                </StyledWrapper>
                {!!Object.keys(categories)?.length && (
                    <Hidden above="md" {...styles.categoryHidden}>
                        {categorySelector}
                    </Hidden>
                )}
                <StyledWrapper {...styles.listWrapper}>
                    <Typography {...styles.listTitle}>{translate("More From The Solutions Desk")}</Typography>
                    {allPages.map(page => (
                        <StyledWrapper {...styles.newsWrapper} key={page.id}>
                            <StyledWrapper {...styles.contentWrapper}>
                                <StyledWrapper {...styles.newsListImageWrapper}>
                                    {page.cardImage && (
                                        <LazyImage
                                            width="301"
                                            height="191"
                                            src={page.cardImage}
                                            {...styles.newsListImage}
                                        />
                                    )}
                                </StyledWrapper>
                                <StyledWrapper {...styles.contentSummaryWrapper}>
                                    <Typography variant="h2" {...styles.titleText}>
                                        <Link
                                            href={page.url}
                                            {...styles.linkStyles}
                                            data-test-selector="newslist_pageTitle"
                                        >
                                            {page.title}
                                        </Link>
                                    </Typography>
                                    {page.cardSummary ? (
                                        <StyledWrapper {...styles.summaryWrapper} data-test-selector="newslist_content">
                                            {parse(page.cardSummary, parserOptions)}
                                        </StyledWrapper>
                                    ) : (
                                        <StyledWrapper {...styles.summaryPlaceholder}></StyledWrapper>
                                    )}
                                    <Link
                                        href={page.url}
                                        {...styles.readMoreLink}
                                        data-test-selector={`newsList_ReadMore_${page.title}`}
                                    >
                                        {translate("Read More")}
                                    </Link>
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.newsListDetails}>
                                <StyledWrapper {...styles.dateAndAuthorWrapper}>
                                    {page.date && (
                                        <Typography
                                            {...styles.publishDateText}
                                            data-test-selector="newslist_publishdate"
                                        >
                                            {page.date}
                                        </Typography>
                                    )}
                                </StyledWrapper>
                                <StyledWrapper>
                                    {page.cardCategory && (
                                        <Typography {...styles.category} data-test-selector="newslist_category">
                                            {translate("categories")}:
                                            <Link
                                                {...styles.listCategoryLink}
                                                onClick={() => filterByCategory(page.cardCategory.toLowerCase())}
                                            >
                                                {page.cardCategory}
                                            </Link>
                                        </Typography>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                    {showLoadMore && !pages.isLoading && (
                        <Button
                            {...styles.loadMore}
                            onClick={() => {
                                currentPage.current += 1;
                                updateParameter({ page: currentPage.current });
                            }}
                        >
                            {translate("Load More Posts")}
                            <Icon src={ChevronDown} {...styles.loadMoreIcon} />
                        </Button>
                    )}
                    {pages?.value && !allPages.length && !pages.isLoading && (
                        <Notify {...styles.notify} type="warning" data-test-selector="newslist_noItems">
                            {siteMessage("Custom_NewsList_NoItems")}
                        </Notify>
                    )}
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.categoryGridItem}>
                {!!Object.keys(categories)?.length && (
                    <Hidden below="lg" {...styles.categoryHidden}>
                        {categorySelector}
                    </Hidden>
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(NewsListView)),
    definition: {
        group: "News List",
        allowedContexts: ["NewsListPage"],
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 8,
                fieldType: "General",
                min: 1,
                max: 20,
            },
        ],
    },
};

export default widgetModule;
